import { Icon16Done } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import image from 'img/TBankImage/Banner.png';
import imageMobile from 'img/TBankImage/BannerMobile.png';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { closePopupHelper, openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { parseURL } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './TBankModal.css';
import { TBANK_PROMO_ID } from './TBankSub.helpers';

const currentUrl = parseURL(window.location.href);
const urlBenefits = `${currentUrl.origin}/promo/quota#benefits`;

const title = 'Подписка Mail Space активна. Вот что у\u00A0вас есть';
const textItems = [
    {
        key: 'autoupload',
        text: 'Безлимит в\u00A0Облаке для фото и\u00A0видео с\u00A0телефона',
    },
    {
        key: 'quota',
        text: '512 гигабайт для остальных файлов',
    },
    {
        key: 'ads',
        text: 'Почта и\u00A0Облако без рекламы',
    },
    {
        key: 'benefits',
        text: 'И\u00A0другие возможности',
        textJsx: (
            <>
                И&nbsp;другие{' '}
                <a href={urlBenefits} className={styles.link} target="_blank" rel="noreferrer">
                    возможности
                </a>
            </>
        ),
    },
];
const buttonText = 'Здорово';

type TBankModalProps = {
    onClose?: () => void;
    onClick?: () => void;
};

const sendAnalytics = (action: 'click' | 'show' | 'close') => {
    sendPaymentGa({
        eventCategory: ECategoryGa.modal,
        action,
        name: 'mnz_success_activate_t_bank',
    });
};

export const TBankModal = memo<TBankModalProps>(({ onClose, onClick }) => {
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        storeHelper.markAsShown(TBANK_PROMO_ID);
        sendAnalytics('show');
    }, []);

    const handleClick = useCallback(() => {
        sendAnalytics('click');
        closeTBankModal();
        onClick?.();
    }, [isMobile, onClick]);

    const handleClose = useCallback(() => {
        storeHelper.markAsShown(TBANK_PROMO_ID);
        sendAnalytics('close');
        onClose?.();
    }, [onClose]);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={isMobile ? 12 : 16} />
                <ul data-qa={'TBankBlock'} className={styles.featureList}>
                    {textItems.map((item) => {
                        return (
                            <li
                                key={item.key}
                                className={classNames(styles.featureListItem, {
                                    [styles.featureListItemMobile]: isMobile,
                                })}
                            >
                                <div className={styles.featureListItemIcon}>
                                    {isMobile ? <Icon16Done /> : <Icon16Done width={20} height={20} />}
                                </div>
                                <div data-qa={'TBankBlock_text'}>{item.textJsx ? item.textJsx : item.text}</div>
                            </li>
                        );
                    })}
                </ul>
                <Spacing size={16} />
            </>
        );
    }, [textItems]);

    if (isMobile) {
        return (
            <MobileDialog
                id="tbank-modal-mobile"
                onClose={handleClose}
                topmost
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
                innerClassNames={{
                    close: styles.closeIconTBankMobile,
                }}
            >
                <div ref={scrollRef}>
                    <img src={imageMobile} alt={title} className={styles.mobileImg} />
                    <div className={styles.textBlockWrapper}>
                        <Text className={styles.titleMobile}>{title}</Text>
                        {content}
                    </div>
                    <div className={styles.buttonWrapper}>
                        <Button primary theme="vk" sizeMode={ButtonSizeMode.extraBig} onClick={handleClick}>
                            {buttonText}
                        </Button>
                    </div>
                </div>
            </MobileDialog>
        );
    }

    return (
        <WhatsNewDialog
            imageUrl={image}
            title={title}
            content={content}
            primaryButtonText={buttonText}
            onClick={handleClick}
            onClose={handleClose}
            imageBgColor="var(--vkui--color_background_accent)"
            buttonTheme="vk"
            dialogSize="large"
            isRebranding
            qaId="TBankModal"
            closeIconClassName={styles.closeIconTBank}
        />
    );
});

TBankModal.displayName = 'TBankModal';

export const openTBankModal = (data?: TBankModalProps) =>
    openPopupHelper({ popupName: popupNames.TBANK_MODAL, data, onClose: data?.onClose });
const closeTBankModal = () => closePopupHelper(popupNames.TBANK_MODAL);
